import { Component } from './component.js';

/**
 * Drop a Hint Component
 * Handles product sharing with variant-specific deep links
 */
export class DropAHintComponent extends Component {
  #currentVariantId = '';
  #currentVariantPrice = '';
  #currentVariantCompareAtPrice = '';
  #currentVariantUrl = '';
  #currentProductTitle = '';
  #currentProductHandle = '';
  #currentVariantImage = '';
  #currentVariantSku = '';
  #currentVariantTitle = '';
  #currentRatingValue = 0;
  #currentReviewCount = 0;
  #isEmailView = false;

  /** @type {((event: CustomEvent) => void) | undefined} */
  #boundHandleVariantChange;

  /** @type {((event: CustomEvent) => void) | undefined} */
  #boundHandleDialogOpen;

  /** @type {HTMLElement | null} */
  #drawerComponent = null;

  /**
   * Define attributes to watch for changes (triggered by morph operations)
   */
  static get observedAttributes() {
    return [
      'data-variant-id',
      'data-variant-price',
      'data-variant-compare-at-price',
      'data-variant-url',
      'data-product-title',
      'data-product-handle',
      'data-variant-image',
      'data-variant-sku',
      'data-variant-title',
      'data-rating-value',
      'data-review-count'
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize variant data
    this.#updateVariantData();

    // Bind the handler once for proper cleanup
    this.#boundHandleVariantChange = this.#handleVariantChange.bind(this);
    this.#boundHandleDialogOpen = this.#handleDialogOpen.bind(this);

    // Listen for variant changes
    document.addEventListener('variant:selected', /** @type {EventListener} */(this.#boundHandleVariantChange));

    // Listen for drawer open event - find drawer-component and listen for dialog:open
    this.#drawerComponent = this.closest('drawer-component');
    if (this.#drawerComponent) {
      this.#drawerComponent.addEventListener('dialog:open', /** @type {EventListener} */(this.#boundHandleDialogOpen));
    }

    // Setup sharing buttons
    this.#setupSharingButtons();

    // Setup email form
    this.#setupEmailForm();

    // Setup back button
    const backButton = this.querySelector('[data-back-to-sharing]');
    if (backButton) {
      backButton.addEventListener('click', this.#showSharingView.bind(this));
    }

    // Initialize view state - show sharing view by default
    this.#showSharingView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#boundHandleVariantChange) {
      document.removeEventListener('variant:selected', /** @type {EventListener} */(this.#boundHandleVariantChange));
    }
    if (this.#boundHandleDialogOpen && this.#drawerComponent) {
      this.#drawerComponent.removeEventListener('dialog:open', /** @type {EventListener} */(this.#boundHandleDialogOpen));
    }
    this.#drawerComponent = null;
  }

  /**
   * Handle attribute changes (triggered by morph operations)
   * This ensures the component re-reads data attributes when DOM is morphed
   * @param {string} name - The attribute name that changed
   * @param {string} oldValue - The previous value
   * @param {string} newValue - The new value
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Only process if value actually changed and component is connected
    if (oldValue === newValue || !this.isConnected) return;

    // Re-read all data attributes when any observed attribute changes
    this.#updateVariantData();

    // Update email preview if visible
    if (this.#isEmailView) {
      this.#updateEmailPreview();
    }
  }

  /**
   * Update variant data from data attributes
   */
  #updateVariantData() {
    this.#currentVariantId = this.dataset.variantId || '';
    this.#currentVariantPrice = this.dataset.variantPrice || '';
    this.#currentVariantCompareAtPrice = this.dataset.variantCompareAtPrice || '';
    this.#currentVariantUrl = this.dataset.variantUrl || '';
    this.#currentProductTitle = this.dataset.variantTitle || this.dataset.productTitle || '';
    this.#currentProductHandle = this.dataset.productHandle || '';
    this.#currentVariantImage = this.dataset.variantImage || '';
    this.#currentVariantSku = this.dataset.variantSku || '';
    this.#currentVariantTitle = this.dataset.variantTitle || '';
    this.#currentRatingValue = parseFloat(this.dataset.ratingValue || '0');
    this.#currentReviewCount = parseInt(this.dataset.reviewCount || '0');
  }

  /**
   * Handle variant change event
   * @param {CustomEvent} event
   */
  #handleVariantChange(event) {
    if (!event.detail?.variant) return;

    const variant = event.detail.variant;
    const productId = this.dataset.productId;

    // Only update if this is for our product
    if (variant.product_id && String(variant.product_id) !== String(productId)) {
      return;
    }

    // Update variant data
    this.#currentVariantId = variant.id ? String(variant.id) : '';
    this.#currentVariantPrice = variant.price ? String(variant.price) : '';
    this.#currentVariantCompareAtPrice = variant.compare_at_price ? String(variant.compare_at_price) : '';
    this.#currentVariantSku = variant.sku || '';
    this.#currentVariantTitle = variant.title || '';

    // Build variant URL
    const baseUrl = window.location.origin + window.location.pathname.split('?')[0];
    this.#currentVariantUrl = `${baseUrl}?variant=${this.#currentVariantId}`;

    // Update variant image
    if (variant.featured_image) {
      this.#currentVariantImage = String(variant.featured_image);
    }

    // Update data attributes
    this.dataset.variantId = this.#currentVariantId;
    this.dataset.variantPrice = this.#currentVariantPrice;
    this.dataset.variantCompareAtPrice = this.#currentVariantCompareAtPrice;
    this.dataset.variantUrl = this.#currentVariantUrl;
    this.dataset.variantSku = this.#currentVariantSku;
    this.dataset.variantTitle = this.#currentVariantTitle;

    // Update email form preview if visible
    if (this.#isEmailView) {
      this.#updateEmailPreview();
    }
  }

  /**
   * Handle dialog open event - reset state when drawer opens
   */
  #handleDialogOpen() {
    // Reset to sharing view when drawer opens
    this.#showSharingView();
  }

  /**
   * Show sharing options view
   */
  #showSharingView() {
    this.#isEmailView = false;
    const sharingView = this.querySelector('[data-sharing-view]');
    const emailView = this.querySelector('[data-email-view]');

    if (sharingView instanceof HTMLElement) sharingView.hidden = false;
    if (emailView instanceof HTMLElement) emailView.hidden = true;

    // Hide success/error messages
    const successMessage = this.querySelector('[data-success-message]');
    const errorMessage = this.querySelector('[data-error-message]');
    if (successMessage instanceof HTMLElement) successMessage.hidden = true;
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;
  }

  /**
   * Show email form view
   */
  #showEmailView() {
    this.#isEmailView = true;
    const sharingView = this.querySelector('[data-sharing-view]');
    const emailView = this.querySelector('[data-email-view]');

    if (sharingView instanceof HTMLElement) sharingView.hidden = true;
    if (emailView instanceof HTMLElement) emailView.hidden = false;

    // Update email preview
    this.#updateEmailPreview();

    // Focus first input
    const firstInput = emailView?.querySelector('input[type="text"], input[type="email"]');
    if (firstInput instanceof HTMLInputElement) {
      requestAnimationFrame(() => {
        firstInput.focus();
      });
    }
  }

  /**
   * Update email preview with current variant data
   */
  #updateEmailPreview() {
    const productImage = this.querySelector('.drop-a-hint__product-image');
    const productTitle = this.querySelector('.drop-a-hint__product-title');
    const productPrice = this.querySelector('.drop-a-hint__product-price');

    if (productImage instanceof HTMLImageElement && this.#currentVariantImage) {
      productImage.src = this.#currentVariantImage;
      productImage.alt = this.#currentProductTitle;
    }

    if (productTitle) {
      productTitle.textContent = this.#currentProductTitle;
    }

    if (productPrice && this.#currentVariantPrice) {
      // Format price (assuming price is in cents)
      const priceInDollars = (parseInt(this.#currentVariantPrice) / 100).toFixed(2);
      productPrice.textContent = `$${priceInDollars}`;
    }

    // Variant details are updated via Liquid template, but we could update here if needed
  }

  /**
   * Setup sharing buttons
   */
  #setupSharingButtons() {
    const buttons = this.querySelectorAll('[data-share-type]');

    buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        const target = event.currentTarget;
        if (target instanceof HTMLElement) {
          const shareType = target.dataset.shareType;
          if (shareType) {
            this.#handleShare(shareType);
          }
        }
      });
    });
  }

  /**
   * Handle share action
   * @param {string} shareType
   */
  #handleShare(shareType) {
    const shareData = this.#buildShareData();

    switch (shareType) {
      case 'email':
        this.#showEmailView();
        break;
      case 'facebook':
        this.#shareFacebook(shareData);
        break;
      case 'twitter':
        this.#shareTwitter(shareData);
        break;
      case 'sms':
        this.#shareSMS(shareData);
        break;
      case 'native':
        this.#shareNative(shareData);
        break;
      case 'copy':
        this.#copyLink(shareData);
        break;
    }
  }

  /**
   * Build share data object
   */
  #buildShareData() {
    const shareText = `${this.#currentProductTitle} - ${this.#formatPrice(this.#currentVariantPrice)}`;

    return {
      url: `${window.location.origin}${this.#currentVariantUrl}`,
      title: this.#currentProductTitle,
      text: shareText,
      image: this.#currentVariantImage
    };
  }

  /**
   * Format price from cents to dollars
   * @param {string} priceInCents
   */
  #formatPrice(priceInCents) {
    const price = parseInt(priceInCents) / 100;
    return `$${price.toFixed(2)}`;
  }

  /**
   * Share on Facebook
   * @param {{url: string, title: string, text: string, image: string}} shareData
   */
  #shareFacebook(shareData) {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}`;
    window.open(url, '_blank');
  }

  /**
   * Share on X (Twitter)
   * @param {{url: string, title: string, text: string, image: string}} shareData
   */
  #shareTwitter(shareData) {
    const text = encodeURIComponent(shareData.text);
    const url = encodeURIComponent(shareData.url);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    window.open(twitterUrl, '_blank');
  }

  /**
   * Share via SMS (mobile only)
   * @param {{url: string, title: string, text: string, image: string}} shareData
   */
  #shareSMS(shareData) {
    const text = encodeURIComponent(`${shareData.text} ${shareData.url}`);
    const smsUrl = `sms:?body=${text}`;
    window.location.href = smsUrl;
  }

  /**
   * Share using native share API
   * @param {{url: string, title: string, text: string, image: string}} shareData
   */
  async #shareNative(shareData) {
    if (!navigator.share) {
      // Fallback to copy link if native share not available
      this.#copyLink(shareData);
      return;
    }

    try {
      await navigator.share({
        title: shareData.title,
        text: shareData.text,
        url: shareData.url
      });
    } catch (error) {
      // User cancelled or error occurred
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  }

  /**
   * Copy link to clipboard
   * @param {{url: string, title: string, text: string, image: string}} shareData
   */
  async #copyLink(shareData) {
    try {
      await navigator.clipboard.writeText(shareData.url);

      // Show temporary success message
      const button = this.querySelector('[data-share-type="copy"]');
      if (button) {
        const originalText = button.querySelector('.drop-a-hint__option-text')?.textContent;
        const textElement = button.querySelector('.drop-a-hint__option-text');

        if (textElement) {
          textElement.textContent = 'Link copied!';
          setTimeout(() => {
            if (textElement) {
              textElement.textContent = originalText || 'Copy Link';
            }
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error copying link:', error);
      // Fallback for older browsers
      this.#fallbackCopyLink(shareData.url);
    }
  }

  /**
   * Fallback copy method for older browsers
   * @param {string} text
   */
  #fallbackCopyLink(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (error) {
      console.error('Fallback copy failed:', error);
    }

    document.body.removeChild(textArea);
  }

  /**
   * Setup email form
   */
  #setupEmailForm() {
    const form = this.querySelector('[data-email-form]');
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', this.#handleEmailSubmit.bind(this));

    // Setup retry button
    const retryButton = this.querySelector('[data-retry-button]');
    if (retryButton) {
      retryButton.addEventListener('click', () => {
        const errorMessage = this.querySelector('[data-error-message]');
        if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;
        form.reset();
      });
    }
  }

  /**
   * Handle email form submission
   * @param {SubmitEvent} event
   */
  async #handleEmailSubmit(event) {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const formData = new FormData(form);

    // Validate form
    if (!this.#validateEmailForm(form)) {
      return;
    }

    // Disable submit button
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }

    // Hide previous messages
    const successMessage = this.querySelector('[data-success-message]');
    const errorMessage = this.querySelector('[data-error-message]');
    if (successMessage instanceof HTMLElement) successMessage.hidden = true;
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;

    try {
      // Prepare Klaviyo event data
      const klaviyoData = {
        "senderName": formData.get('sender_name'),
        "senderEmail": formData.get('sender_email'),
        "recipientName": formData.get('recipient_name'),
        "recipientEmail": formData.get('recipient_email'),
        "personalNote": formData.get('note') || '',
        "sendCopyToSender": formData.get('send_copy') === 'on',
        "marketingConsent": formData.get('marketing_consent') === 'on',
        "product": {
          "handle": this.#currentProductHandle,
          "title": this.#currentProductTitle,
          "imageUrl": 'https:' + this.#currentVariantImage,
          "productUrl": `${window.location.origin}${this.#currentVariantUrl}`,
          "price": (parseInt(this.#currentVariantPrice) / 100).toFixed(2),
          "compareAtPrice": this.#currentVariantCompareAtPrice ? (parseInt(this.#currentVariantCompareAtPrice) / 100).toFixed(2) : "0.00",
          "variantId": this.#currentVariantId,
          "variantTitle": this.#currentVariantTitle,
          "sku": this.#currentVariantSku,
          "averageRating": this.#currentRatingValue,
          "totalRating": this.#currentReviewCount
        }
      };

      // Send to Klaviyo
      const callApi = await this.#sendToKlaviyo(klaviyoData);

      if (callApi.success) {
        // Show success message
        if (successMessage instanceof HTMLElement) {
          successMessage.hidden = false;
          const successText = successMessage.querySelector('[data-success-text]');
          if (successText) {
            successText.textContent = 'Your request has been sent';
          }

          setTimeout(() => {
            successMessage.hidden = true;
          }, 5000);
        }
        // Reset form
        form.reset();
      } else {
        // Handle API error
        if (errorMessage instanceof HTMLElement) {
          errorMessage.hidden = false;
          const errorText = errorMessage.querySelector('[data-error-text]');
          if (errorText) {
            errorText.textContent = callApi.message || 'An error occurred. Please try again.';
          }

          setTimeout(() => {
            errorMessage.hidden = true;
          }, 5000);
        }
      }

    } catch (error) {
      console.error('Error sending email:', error);

      // Show error message for network/system failures
      if (errorMessage instanceof HTMLElement) {
        errorMessage.hidden = false;
        const errorText = errorMessage.querySelector('[data-error-text]');
        if (errorText) {
          errorText.textContent = error.message || 'An error occurred. Please try again.';
        }

        setTimeout(() => {
          errorMessage.hidden = true;
        }, 5000);
      }
    } finally {
      // Re-enable submit button
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        submitButton.textContent = 'Send';
      }
    }
  }

  /**
   * Validate email form
   * @param {HTMLFormElement} form
   */
  #validateEmailForm(form) {
    let isValid = true;
    const requiredFields = [
      'sender_name',
      'sender_email',
      'recipient_name',
      'recipient_email'
    ];

    requiredFields.forEach(fieldName => {
      const field = form.querySelector(`[name="${fieldName}"]`);
      const errorElement = form.querySelector(`#drop-a-hint-${fieldName.replace('_', '-')}-error`);

      if (!(field instanceof HTMLInputElement)) return;

      // Remove previous error state
      field.setAttribute('aria-invalid', 'false');
      if (errorElement instanceof HTMLElement) {
        errorElement.hidden = true;
        errorElement.textContent = '';
      }

      // Validate field
      if (!field.value.trim()) {
        isValid = false;
        field.setAttribute('aria-invalid', 'true');
        if (errorElement instanceof HTMLElement) {
          errorElement.textContent = 'This field is required';
          errorElement.hidden = false;
        }
      } else if (field.type === 'email' && !this.#isValidEmail(field.value)) {
        isValid = false;
        field.setAttribute('aria-invalid', 'true');
        if (errorElement instanceof HTMLElement) {
          errorElement.textContent = 'Please enter a valid email address';
          errorElement.hidden = false;
        }
      }
    });

    return isValid;
  }

  /**
   * Validate email format
   * @param {string} email
   */
  #isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Send data to Klaviyo
   * @param {object} data
   */
  async #sendToKlaviyo(data) {
    // Check if Klaviyo is loaded
    // @ts-ignore - Klaviyo global variable

    const response = await fetch('/apps/common/v1/drop-hint/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return await response.json();
  }
}

customElements.define('drop-a-hint-component', DropAHintComponent);
